import { useEffect, useMemo, useState, useRef } from 'react'
import { Button as ShadcnButton } from '@/components/ui'
import continueBlue from '@/assets/images/media-buying/continue-blue.png'
import viewWebsite from '@/assets/images/media-buying/view-website.png'
import submitButton from '@/assets/images/media-buying/submit.png'
import { PhoneFormMockup } from './PhoneFormMockup'
import { ChevronIcon } from '@/components/icons'

export const LeadFormQuestionsView = ({
    questions,
    pageIcon,
    pageTitle,
    hideSelectForm,
    className,
    headline,
    description,
    // New props for form data
    contextCard,
    thankYouPage,
    privacyPolicyUrl,
    legalContent,
    followUpActionText,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [direction, setDirection] = useState('forward')
    const scrollableRef = useRef(null)

    // Memoize slides calculation to prevent infinite re-renders
    const allSlides = useMemo(() => {
        if (!questions || !Array.isArray(questions)) return []

        // Separate system fields (contact info) from regular questions
        // System fields: EMAIL, FULL_NAME, POST_CODE, etc. (have type but no question/options)
        const systemFields = questions.filter(
            (q) => q.type && q.type !== 'CUSTOM' && !q.question && !q.options && !q.privacy && !q.thankyou,
        )

        // Regular questions: custom questions, privacy, thank you
        const regularQuestions = questions.filter(
            (q) =>
                q.question || // Has a question text
                q.privacy || // Privacy screen
                q.thankyou || // Thank you screen
                q.type === 'CUSTOM' || // Custom question type
                (q.options && q.options.length > 0), // Has multiple choice options
        )

        // Create slides: contact info slide + regular questions
        const slides = []
        if (systemFields.length > 0) {
            slides.push({ type: 'contact_info', fields: systemFields })
        }
        slides.push(...regularQuestions)

        return slides
    }, [questions])

    const total = allSlides.length

    // Memoize current object to prevent constant re-creation
    const current = useMemo(() => {
        return allSlides[currentIndex] || {}
    }, [allSlides, currentIndex])

    const handleNext = () => {
        if (currentIndex < total - 1) {
            setDirection('forward')
            setCurrentIndex((i) => i + 1)
            // Scroll to top when changing questions
            if (scrollableRef.current) {
                scrollableRef.current.scrollTop = 0
            }
        }
    }

    const handleBack = () => {
        if (currentIndex > 0) {
            setDirection('back')
            setCurrentIndex((i) => i - 1)
            // Scroll to top when changing questions
            if (scrollableRef.current) {
                scrollableRef.current.scrollTop = 0
            }
        }
    }

    const progress = ((currentIndex + 1) / total) * 100

    useEffect(() => {
        if (questions && questions.length > 0) {
            setCurrentIndex(0)
            setDirection('forward')
        }
    }, [questions])

    const getButtonForQuestion = useMemo(() => {
        if (current.thankyou) {
            return viewWebsite
        } else if (current.privacy) {
            return submitButton
        } else {
            return continueBlue
        }
    }, [current.thankyou, current.privacy])

    return (
        <div className={`${className} phone-form-container position-relative`}>
            {/* Empty State - Show instead of mock phone when no questions */}
            {(!questions || questions.length < 1) && !hideSelectForm ? (
                <div className="ps-1 d-flex tw:justify-content-center tw:align-items-start tw:pe-1 tw:pb-2 tw:overflow-x-hidden">
                    <div
                        className="phone-form-mockup border tw:!bg-transparent tw:!border-blue-500 tw:rounded d-flex flex-column align-items-center justify-content-center tw:text-dark tw:pt-8"
                        style={{ minHeight: '429px', maxHeight: '429px' }}
                    >
                        <div className="d-flex flex-column align-items-center justify-content-center">
                            <div className="tw:bg-slate-700 tw:rounded-full tw:pl-4 tw:pt-1 tw:mb-5 tw:w-[80px] tw:h-[80px]">
                                <i className="bi bi-file-earmark-text tw:text-white" style={{ fontSize: '48px' }}></i>
                            </div>
                            <span className="tw:text-white tw:text-sm tw:font-medium tw:text-center tw:px-4">
                                To show the preview please select a form from the list
                            </span>
                        </div>
                    </div>
                </div>
            ) : (
                <PhoneFormMockup
                    scrollableRef={scrollableRef}
                    current={current}
                    direction={direction}
                    pageIcon={pageIcon}
                    pageTitle={pageTitle}
                    contextCard={contextCard}
                    headline={headline}
                    description={description}
                    thankYouPage={thankYouPage}
                    privacyPolicyUrl={privacyPolicyUrl}
                    legalContent={legalContent}
                    progress={progress}
                    getButtonForQuestion={getButtonForQuestion}
                    followUpActionText={followUpActionText}
                />
            )}
            {/* Bottom Navigation - Only show when there are questions */}
            {questions && questions.length > 0 && (
                <div className="d-flex justify-content-between align-items-center pt-4 pb-3">
                    <span className="tw:text-sm">
                        {current.type === 'contact_info' ? 'Prefill information' : 'Custom Questions'}{' '}
                    </span>
                    <div className="tw:flex tw:gap-2 tw:items-center">
                        <span className="tw:text-sm">
                            {currentIndex + 1} of {total}
                        </span>
                        <ShadcnButton
                            variant="secondary"
                            className="tw:w-7 tw:h-7 tw:px-0 tw:has-[>svg]:px-0"
                            onClick={handleBack}
                            disabled={currentIndex === 0}
                        >
                            <ChevronIcon className="tw:size-5 tw:rotate-90" />
                        </ShadcnButton>
                        <ShadcnButton
                            variant="secondary"
                            className="tw:w-7 tw:h-7 tw:px-0 tw:has-[>svg]:px-0"
                            onClick={handleNext}
                            disabled={currentIndex === total - 1}
                        >
                            <ChevronIcon className="tw:size-5 tw:rotate-270" />
                        </ShadcnButton>
                    </div>
                </div>
            )}
        </div>
    )
}
